import { FormattedMessage } from 'react-intl';
import { useParams } from 'react-router-dom';

import { PERMISSION } from 'src/features/auth';
import EntitySubgroupsTable from 'src/features/entity/components/table/EntitySubgroupsTable';
import PeopleTable from 'src/features/entity/components/table/PeopleTable';
import { Entity, ENTITY_TYPE } from 'src/features/entity/types';
import { entityMessages } from 'src/features/entity/utils/messages';
import GatewayPassesTable from 'src/features/gatewayPass/components/GatewayPassesTable';
import IdentifiersTable from 'src/features/identifiers/components/tables/IdentifiersTable';
import MembershipsTable from 'src/features/membership/components/table/MembershipsTable';
import { membershipMessages } from 'src/features/membership/utils/messages';
import PaymentsTable from 'src/features/payment/components/tables/PaymentsTable';
import PastStaysTable from 'src/features/stay/components/PastStaysTable';
import useHasPermissions from 'src/hooks/useHasPermissions';
import { Tabs, TabsContent, TabsList, TabsTrigger } from 'src/registry/ui/Tabs';

type EntityDashboardTablesProps = {
    entityData?: Entity;
};

const EntityDashboardTables = ({ entityData }: EntityDashboardTablesProps) => {
    const { type = '' } = useParams();

    const permissionAccessMemberships = useHasPermissions([PERMISSION.ACCESS_MEMBERSHIPS]);
    const permissionAccessPayments = useHasPermissions([PERMISSION.ACCESS_PAYMENTS]);
    const permissionAccessGatewayPasses = useHasPermissions([PERMISSION.ACCESS_GATEWAY_PASSES]);
    const permissionAccessPastStays = useHasPermissions([PERMISSION.ACCESS_PAST_STAYS]);
    const permissionAccessVisitors = useHasPermissions([PERMISSION.ACCESS_VISITORS]);
    const permissionAccessEntities = useHasPermissions([PERMISSION.ACCESS_ENTITIES]);

    const defaultTabValue = (() => {
        if (permissionAccessVisitors) return 'identifiers';
        if (permissionAccessEntities && type === ENTITY_TYPE.ORGANISATION) return 'people';
        if (permissionAccessEntities && type === ENTITY_TYPE.ORGANISATION) return 'subgroups';
        if (permissionAccessMemberships) return 'memberships';
        if (permissionAccessPastStays) return 'past-stays';
        if (permissionAccessGatewayPasses) return 'gateway-passes';
        if (permissionAccessPayments) return 'payments';
    })();

    const showTabsAndTables =
        permissionAccessMemberships ||
        permissionAccessPayments ||
        permissionAccessGatewayPasses ||
        permissionAccessPastStays ||
        permissionAccessVisitors ||
        permissionAccessEntities;

    if (!entityData) return null;
    return (
        <div className="grid flex-3 grid-cols-1 pr-8">
            {showTabsAndTables && (
                <Tabs defaultValue={defaultTabValue}>
                    <TabsList>
                        {permissionAccessVisitors && (
                            <TabsTrigger value="identifiers">
                                <FormattedMessage {...membershipMessages.identifiers} />
                            </TabsTrigger>
                        )}
                        {permissionAccessEntities && type === ENTITY_TYPE.ORGANISATION && (
                            <TabsTrigger value="people">
                                <FormattedMessage {...entityMessages.people} />
                            </TabsTrigger>
                        )}
                        {permissionAccessEntities && type === ENTITY_TYPE.ORGANISATION && (
                            <TabsTrigger value="subgroups">
                                <FormattedMessage {...entityMessages.subgroups} />
                            </TabsTrigger>
                        )}
                        {permissionAccessMemberships && (
                            <TabsTrigger value="memberships">
                                <FormattedMessage {...membershipMessages.memberships} />
                            </TabsTrigger>
                        )}
                        {permissionAccessPastStays && (
                            <TabsTrigger value="past-stays">
                                <FormattedMessage {...membershipMessages.pastStays} />
                            </TabsTrigger>
                        )}
                        {permissionAccessGatewayPasses && (
                            <TabsTrigger value="gateway-passes">
                                <FormattedMessage {...membershipMessages.gatewayPasses} />
                            </TabsTrigger>
                        )}
                        {permissionAccessPayments && (
                            <TabsTrigger value="payments">
                                <FormattedMessage {...membershipMessages.payments} />
                            </TabsTrigger>
                        )}
                    </TabsList>

                    {permissionAccessVisitors && (
                        <TabsContent value="identifiers" className="w-full">
                            <IdentifiersTable entityId={entityData.id} />
                        </TabsContent>
                    )}

                    {permissionAccessEntities && type === ENTITY_TYPE.ORGANISATION && (
                        <TabsContent value="people" className="w-full">
                            <PeopleTable parentId={entityData.id} />
                        </TabsContent>
                    )}
                    {permissionAccessEntities && type === ENTITY_TYPE.ORGANISATION && (
                        <TabsContent value="subgroups" className="w-full">
                            <EntitySubgroupsTable parentEntity={entityData} />
                        </TabsContent>
                    )}
                    {permissionAccessMemberships && (
                        <TabsContent value="memberships" className="w-full">
                            <MembershipsTable entityId={entityData.id} />
                        </TabsContent>
                    )}
                    {permissionAccessPastStays && (
                        <TabsContent value="past-stays" className="w-full">
                            <PastStaysTable entityId={entityData.id} />
                        </TabsContent>
                    )}
                    {permissionAccessGatewayPasses && (
                        <TabsContent value="gateway-passes" className="w-full">
                            <GatewayPassesTable entityId={entityData.id} />
                        </TabsContent>
                    )}
                    {permissionAccessPayments && (
                        <TabsContent value="payments" className="w-full">
                            <PaymentsTable entityId={entityData.id} />
                        </TabsContent>
                    )}
                </Tabs>
            )}
        </div>
    );
};

export default EntityDashboardTables;
